/**
 * Concierges Page - Concierge Services Management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Plus, Search, MoreVertical, Building2,
  RefreshCw, Mail, Phone, Eye
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { api } from '../../App';
import { toast } from 'sonner';

const ConciergesPage = () => {
  const navigate = useNavigate();
  const [concierges, setConcierges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchConcierges();
  }, [statusFilter]);

  const fetchConcierges = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);

      const { data } = await api.get(`/admin/concierges?${params}`);
      setConcierges(data.concierges);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConcierges();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Conciergeries</h1>
          <p className="text-muted-foreground">Services de conciergerie multi-riads</p>
        </div>
        <Button onClick={() => navigate('/admin/customers')}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Conciergerie
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="ACTIVE">Actifs</SelectItem>
            <SelectItem value="SUSPENDED">Suspendus</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Concierge List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {concierges.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune conciergerie trouvée</p>
              <p className="text-sm text-muted-foreground mt-1">
                Créez une conciergerie depuis la page Clients
              </p>
            </div>
          ) : (
            concierges.map(concierge => (
              <Card 
                key={concierge.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/concierges/${concierge.id}`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-purple-600" />
                    </div>
                    <Badge variant={concierge.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {concierge.status}
                    </Badge>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1">{concierge.name}</h3>
                  
                  <div className="space-y-1 text-sm text-muted-foreground mb-4">
                    {concierge.email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {concierge.email}
                      </p>
                    )}
                    {concierge.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {concierge.phone}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{concierge.managed_riad_count}</span>
                      <span className="text-sm text-muted-foreground">riads gérés</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/concierges/${concierge.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir détails
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ConciergesPage;
